<template>
  <div class="user-permissions">
    <NiceSlot :title="$gettext('Permissions')">
      <div class="margin-bottom-10">
        <button class="btn btn-small btn-default btn-plain" type="button" @click="setAll(true)">{{ $pgettext("Permissions", "All") }}</button>
        <button class="btn btn-small btn-default btn-plain" type="button" @click="setAll(false)">{{ $gettext("None") }}</button>
      </div>

      <div class="permissions">
        <div class="permission-group" v-for="group in permissionGroups" :key="group.id">
          <NiceCheckbox
            class="permission-title-checkbox"
            :model-value="group.isSelected"
            :indeterminate="group.isIndeterminate"
            :title="group.name"
            @change="permissionGroupToggle(group)"
          />

          <div class="children-list" v-for="child in group.children" :key="child.id">
            <NiceCheckbox
              class="permission-checkbox"
              :title="child.text"
              :description="child.description"
              :model-value="child.isSelected"
              @change="permissionToggle(child.id, child.isSelected)"
            />
          </div>
        </div>
      </div>
    </NiceSlot>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from "vue";
import CommonService from "@/services/common.service";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:modelValue"]);

const permissionGroups = computed(() => {
  const schema = CommonService.permissions; // Assuming this returns your array of groups

  return schema.map((group) => {
    const childrenIds = group.children.map((c) => c.id);
    const selectedChildren = childrenIds.filter((id) => !!props.modelValue[id]);

    return {
      ...group,
      isSelected: selectedChildren.length === childrenIds.length,
      isIndeterminate: selectedChildren.length > 0 && selectedChildren.length < childrenIds.length,
      children: group.children.map((child) => ({
        ...child,
        isSelected: !!props.modelValue[child.id],
      })),
    };
  });
});

function updatePermissions(newPermissions) {
  emit("update:modelValue", { ...props.modelValue, ...newPermissions });
}

function permissionToggle(id, currentState) {
  updatePermissions({ [id]: !currentState });
}

function permissionGroupToggle(group) {
  const targetState = !getGroupSelectedState(group);
  const updates = {};
  group.children.forEach((child) => {
    updates[child.id] = targetState;
  });
  updatePermissions(updates);
}

function setAll(value) {
  const updates = {};
  CommonService.permissions.forEach((group) => {
    group.children.forEach((child) => {
      updates[child.id] = value;
    });
  });
  emit("update:modelValue", updates);
}

// Helper to check group state for the toggle logic
function getGroupSelectedState(group) {
  return group.children.every((child) => !!props.modelValue[child.id]);
}
</script>

<style lang="scss" scoped>
.permissions {
  .permission-group {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--nice-border-color);
  }

  .permission-title {
    font-weight: bold;
  }

  .permission-checkbox {
    margin-left: 1rem;
  }

  .permission-title-checkbox {
    font-weight: bold;
  }
}
</style>
